import React from 'react'
import { Box, type BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

import { artifactReview, typography } from '../../tokens'

/**
 * A typography-only boundary for generated artifacts.
 *
 * Artifact renderers historically accumulated local 10–13px declarations. This
 * scoped surface restores a document reading scale without changing the global
 * application theme or visualization-owned SVG/canvas text.
 */
const StyledArtifactReadingSurface = styled(Box, {
  shouldForwardProp: prop => prop !== 'expanded',
})<{ expanded: boolean }>(({ theme, expanded }) => ({
  minWidth: 0,
  color: theme.palette.text.primary,
  fontFamily: typography.artifact.body.fontFamily,
  fontSize: typography.artifact.body.fontSize,
  fontWeight: typography.artifact.body.fontWeight,
  lineHeight: typography.artifact.body.lineHeight,
  letterSpacing: typography.artifact.body.letterSpacing,

  '& p, & li, & dd': {
    fontFamily: typography.artifact.body.fontFamily,
    fontSize: typography.artifact.body.fontSize,
    lineHeight: typography.artifact.body.lineHeight,
  },
  '& dt, & small, & figcaption': {
    fontFamily: typography.artifact.supporting.fontFamily,
    fontSize: typography.artifact.supporting.fontSize,
    lineHeight: typography.artifact.supporting.lineHeight,
  },
  '& h1': {
    fontFamily: typography.artifact.title.fontFamily,
    fontWeight: typography.artifact.title.fontWeight,
    lineHeight: typography.artifact.title.lineHeight,
  },
  '& h2, & h3, & h4, & h5, & h6': {
    fontFamily: typography.artifact.sectionTitle.fontFamily,
    lineHeight: typography.artifact.sectionTitle.lineHeight,
  },

  '& .MuiTypography-root': {
    fontFamily: typography.artifact.body.fontFamily,
  },
  '& .MuiTypography-h4': {
    ...typography.artifact.title,
  },
  '& .MuiTypography-h5, & .MuiTypography-h6': {
    ...typography.artifact.sectionTitle,
  },
  '& .MuiTypography-subtitle1, & .MuiTypography-subtitle2': {
    ...typography.artifact.body,
    fontWeight: typography.fontWeight.semibold,
  },
  '& .MuiTypography-body1': {
    ...typography.artifact.body,
  },
  '& .MuiTypography-body2': {
    ...typography.artifact.bodyCompact,
  },
  '& .MuiTypography-caption, & .MuiTypography-overline': {
    ...typography.artifact.supporting,
  },

  '& .MuiChip-root': {
    height: 'auto',
    minHeight: 28,
  },
  '& .MuiChip-label': {
    ...typography.artifact.label,
    paddingTop: 3,
    paddingBottom: 3,
    whiteSpace: 'normal',
  },
  '& .MuiTableCell-root': {
    ...typography.artifact.table,
    padding: '10px 12px',
    verticalAlign: 'top',
  },
  '& .MuiTableCell-head': {
    ...typography.artifact.label,
    color: theme.palette.text.secondary,
  },
  '& table th, & table td': {
    ...typography.artifact.table,
  },
  '& table th': {
    ...typography.artifact.label,
  },
  '& .MuiButton-root:not([data-workspace-action])': {
    fontFamily: typography.artifact.bodyCompact.fontFamily,
    fontSize: typography.artifact.bodyCompact.fontSize,
    lineHeight: 1.35,
  },
  // Review controls use the artifact floor; code chrome keeps WorkspaceAction's
  // existing compact desktop and larger touch-target contracts.
  '& button:not([data-workspace-action])': {
    fontFamily: typography.artifact.bodyCompact.fontFamily,
    fontSize: typography.artifact.bodyCompact.fontSize,
    lineHeight: 1.35,
    minWidth: artifactReview.controlMinSize,
    minHeight: artifactReview.controlMinSize,
  },
  '& .MuiInputBase-input': {
    fontFamily: typography.artifact.body.fontFamily,
    fontSize: typography.artifact.body.fontSize,
    lineHeight: 1.5,
  },
  '& .MuiFormLabel-root, & .MuiInputLabel-root': {
    ...typography.artifact.label,
  },
  // MUI shrinks floating labels to 75%; keep the rendered label at the 14px scale.
  '& .MuiInputLabel-shrink': {
    fontSize: `calc(${typography.artifact.label.fontSize} / 0.75)`,
  },
  '& .MuiOutlinedInput-notchedOutline legend': {
    fontSize: typography.artifact.label.fontSize,
  },

  '& pre, & code': {
    fontFamily: typography.artifact.code.fontFamily,
    fontSize: typography.artifact.code.fontSize,
    lineHeight: typography.artifact.code.lineHeight,
  },
  '& .prose': {
    fontFamily: typography.artifact.body.fontFamily,
    fontSize: typography.artifact.body.fontSize,
    lineHeight: typography.artifact.body.lineHeight,
  },
  '& .prose p, & .prose li': {
    maxWidth: expanded ? 'none' : '76ch',
  },
  '& .text-xs, & .text-sm': {
    fontSize: typography.artifact.supporting.fontSize,
    lineHeight: typography.artifact.supporting.lineHeight,
  },

  '& [data-artifact-role="title"]': {
    ...typography.artifact.title,
  },
  '& [data-artifact-role="section-title"]': {
    ...typography.artifact.sectionTitle,
  },
  '& [data-artifact-role="body"], & [data-artifact-role="value"]': {
    ...typography.artifact.body,
  },
  '& [data-artifact-role="supporting"]': {
    ...typography.artifact.supporting,
  },
  '& [data-artifact-role="label"]': {
    ...typography.artifact.label,
  },
  '& [data-artifact-role="metric"]': {
    ...typography.artifact.metric,
  },
  '& [data-artifact-role="identifier"]': {
    ...typography.artifact.code,
    fontSize: typography.artifact.bodyCompact.fontSize,
    overflowWrap: 'anywhere',
  },
}))

export type ArtifactReadingSurfaceProps = BoxProps & { expanded?: boolean }

export const ArtifactReadingSurface = React.forwardRef<
  HTMLDivElement,
  ArtifactReadingSurfaceProps
>(function ArtifactReadingSurface({ children, expanded = false, ...props }, ref) {
  return (
    <StyledArtifactReadingSurface
      ref={ref}
      data-artifact-reading-surface
      expanded={expanded}
      {...props}
    >
      {children}
    </StyledArtifactReadingSurface>
  )
})

export default ArtifactReadingSurface
